import { useState, useEffect, useMemo, useCallback } from "react";
import { OrganizationUserService, OrganizationUserVM } from "@propertelligent/client-api";
import { infiniteScrollHook } from "../../../hooks/infiniteScrollHook";
import UserCardItem from "./UserCardItem";
import { useDebounce } from "use-debounce";
import RightSideModal from "../../../newComponents/Modal/right-side-modal";
import Commonpopup from "../../../newComponents/Common-popup/CommonPopup";
import Card from "../../../newComponents/card";
import { FormProvider, useForm } from "react-hook-form";
import InputField from "../../../newComponents/form-fields/InputField";
import SelectField from "../../../newComponents/form-fields/SelectField";
import apiHelper from "../../../utils/apiHelper";
import { UserLoginData } from "../../../components/common/charts/Enums";
import ManageUsersTopHeader from "./ManageUsersTopHeader";
import { GetImages } from "../../../utils/GetImages";
import PhoneInput from "../../../newComponents/form-fields/PhoneInput";
import EmailInput from "../../../newComponents/form-fields/EmailInput";
import { PermissionGuard } from "../../../newComponents/PermissionGuard";
import NoAccess from "../../../pages/NoAccess";
import { AccessLevelEnum } from "@propertelligent/client-api";
import { usePermissions } from "../../../hooks/usePermissions";
import { getEmptyManageUserFormValues } from "./manageUserFormDefaults";
import RoleAccessMatrix from "./RoleAccessMatrix";
import { ALL_FILTER_KEY, resolveAllFilterCount, useAggregateFilterCounts } from "../../../hooks/useAggregateFilterCounts";
import { getApiOrganizationUserOrganizationlistCounts } from "../../../services/listFilterCounts.service";
import { escapeDynamicLinqStringLiteral } from "../../../utils/dynamicLinq";
import { useWhatsAppEnabled } from "../../../hooks/useWhatsAppEnabled";

interface Option {
  Id: string | number;
  Name: string;
}

interface ManageUsersProps {
  className?: string;
}

const ManageUsersPage = ({ className = "" }: ManageUsersProps) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [activeFilter, setActiveFilter] = useState(ALL_FILTER_KEY);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<OrganizationUserVM | undefined>(undefined);
  const [isRoleInfoOpen, setIsRoleInfoOpen] = useState(false);
  const organizationId = Number(sessionStorage.getItem(UserLoginData.organization_id));

  const check = usePermissions();
  const canWrite = check.canWrite("ManageUsers");

  // For Empty State
  const [hasAnyUsers, setHasAnyUsers] = useState<boolean>(false);
  const whatsAppEnabled = useWhatsAppEnabled();

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

  const [userRoles, setUserRoles] = useState<Option[]>([]);
  const searchOnlyFilter = useMemo(
    () =>
      debouncedSearchTerm
        ? `(User.FirstName + " " + User.LastName).ToLower().Contains("${escapeDynamicLinqStringLiteral(debouncedSearchTerm.toLowerCase())}")`
        : null,
    [debouncedSearchTerm],
  );

  const roleIdByName = useMemo(
    () =>
      userRoles.reduce<Record<string, number>>((acc, role) => {
        acc[String(role.Name)] = Number(role.Id);
        return acc;
      }, {}),
    [userRoles],
  );

  const fullyQualifiedFilter = useMemo(() => {
    const filters: string[] = [];
    if (searchOnlyFilter) {
      filters.push(searchOnlyFilter);
    }

    const roleId = roleIdByName[activeFilter];
    if (activeFilter !== ALL_FILTER_KEY && roleId) {
      filters.push(`OrganizationUserTypeId == ${roleId}`);
    }

    return filters.length > 0 ? filters.join(" && ") : null;
  }, [activeFilter, roleIdByName, searchOnlyFilter]);

  const {
    rows,
    loading,
    hasMore,
    rowCount,
    fetchData,
    currentPage,
  } = infiniteScrollHook({
    httpFetchDataFn: OrganizationUserService.getApiOrganizationUserOrganizationlist,
    FullyQualifiedFilters: fullyQualifiedFilter,
    FullyQualifiedSorts: `User.FirstName ${sortDirection}`,
    pageSize: 10,
  });

  const fetchUserRoles = useCallback(() => {
    OrganizationUserService.getApiOrganizationUserUserRoles()
      .then((res) => {
        if (res?.success) {
          const mappedRoles = res.success.map((role: any) => ({
            Id: role.id,
            Name: role.name,
          }));
          setUserRoles(mappedRoles);
        }
      })
      .catch((err) => {
        console.error("Error fetching user roles:", err);
      });
  }, []);

  useEffect(() => {
    fetchUserRoles();
  }, [fetchUserRoles]);

  const fetchCounts = useCallback(async (filter: string | null) => {
    const res = await apiHelper(
      getApiOrganizationUserOrganizationlistCounts,
      { showNotification: false },
      organizationId,
      1,
      1,
      undefined,
      undefined,
      undefined,
      filter,
      null,
    );
    return res?.data?.aggregateValues ?? undefined;
  }, [organizationId]);

  const {
    aggregateValues: aggregateCounts,
    baselineAggregateValues,
    refreshCounts,
    getVisibleOptions,
  } = useAggregateFilterCounts({
    fetchCounts,
    searchOnlyFilter,
    scopeKey: `manage-users:${organizationId}`,
  });

  const visibleFilterOptions = useMemo(
    () =>
      getVisibleOptions([
        { title: ALL_FILTER_KEY, count: resolveAllFilterCount(aggregateCounts) },
        ...userRoles.map((role) => ({ title: role.Name })),
      ]),
    [aggregateCounts, getVisibleOptions, userRoles],
  );

  // Threaded through to UserCardItem so its Delete action can refresh both the row list and
  // the pill-count baseline (a delete changes the total, so page 1 - not the current page,
  // which could now be past the end - plus a fresh aggregate fetch is the correct refresh).
  // Mirrors ContractorList.tsx/payee-accounts/index.tsx's own handleFullRefresh.
  const handleFullRefresh = useCallback(() => {
    refreshCounts();
    fetchData(1);
  }, [refreshCounts, fetchData]);

  // Sort change handler
  const handleSortChange = (direction: "asc" | "desc") => {
    setSortDirection(direction);
  };

  useEffect(() => {
    // Tolerate a failed/in-flight counts fetch (apiHelper swallows the error) by also
    // trusting the row-list's own count, so a single failing counts call can't hide the
    // whole page behind the empty state while rows actually loaded fine.
    setHasAnyUsers(resolveAllFilterCount(baselineAggregateValues) > 0 || rowCount > 0);
  }, [baselineAggregateValues, rowCount]);

  const emptyFormValues = getEmptyManageUserFormValues(organizationId);

  const methods = useForm<OrganizationUserVM>({
    defaultValues: emptyFormValues,
    mode: "onChange",
  });

  const { handleSubmit, reset, formState: { isDirty, isValid } } = methods;

  const openAddUserModal = () => {
    setSelectedUser(undefined);
    reset(getEmptyManageUserFormValues(organizationId));
    setAddModalOpen(true);
  };

  const onSubmit = async (data: OrganizationUserVM) => {
    try {
      const isEdit = !!data.id;

      // ----- PAYLOAD SETUP -----
      const payload = {

        ...(isEdit && { id: data.id }),
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        title: data.title,
        organizationUserTypeId: data.organizationUserTypeId,
        organizationId: organizationId,
        ...(isEdit && { organizationUserId: data.organizationUserId }) // only for PUT
      };


      if (isEdit) {
        const res = await apiHelper(OrganizationUserService.putApiOrganizationUser, {
          title: "User Updated",
          successMessage: "User update successfully",
          failureMessage: "Unable to update user",
          showNotification: true,
          successIcon: <img src={GetImages.toastUserIcon} alt="success" width="20" height="20" />
        }, data.organizationUserId, payload);

        if (res?.data) {
          // If the edited user is the currently logged-in user, refresh the cached
          // display name so the dashboard/top-bar reflect it without a re-login.
          if (String(data.id) === sessionStorage.getItem(UserLoginData.user_id)) {
            sessionStorage.setItem(
              UserLoginData.user_name,
              `${data.firstName} ${data.lastName}`
            );
          }
          setAddModalOpen(false);
          setSelectedUser(undefined);
          reset(getEmptyManageUserFormValues(organizationId));
          refreshCounts();
          fetchData(1);
        }
      } else {
        const res = await apiHelper(OrganizationUserService.postApiOrganizationUser, {
          title: "User Added",
          successMessage: "User added successfully",
          failureMessage: "Unable to add user",
          showNotification: true,
          successIcon: <img src={GetImages.toastUserIcon} alt="success" width="20" height="20" />
        }, payload);

        if (res?.data) {
          setAddModalOpen(false);
          setSelectedUser(undefined);
          reset(getEmptyManageUserFormValues(organizationId));
          refreshCounts();
          fetchData(1);
        }
      }

    } catch (error) {
      console.error("Error submitting user:", error);
    }
  };


  const handleEditUser = (user: OrganizationUserVM) => {
    setSelectedUser(user);
    // keepDefaultValues: edit must not overwrite blank defaults used by Add User
    methods.reset(user, { keepDefaultValues: true });
    setAddModalOpen(true);
  };

  const handleModalClose = () => {
    setAddModalOpen(false);
    setSelectedUser(undefined);
    reset(getEmptyManageUserFormValues(organizationId));
  };

  const formId = selectedUser ? "edit-user-form" : "add-user-form";

  return (
    <PermissionGuard permission="ManageUsers" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
      <div className={`manage-users-page content-wrapper container-fluid ${className}`}>
        {hasAnyUsers && (
          <ManageUsersTopHeader
            PageHeading="Users"
            Count={rowCount}
            ButtonName={canWrite ? "Add User" : undefined}
            MobileButtonName={canWrite ? "Add" : undefined}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onClick={openAddUserModal}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            activeFilter={activeFilter}
            handleFilterChange={setActiveFilter}
            filterOptions={visibleFilterOptions}
          />
        )}

        <UserCardItem
          dataHookState={{
            rows,
            loading,
            hasMore,
            fetchData,
            currentPage,
            handleFullRefresh,
          }}
          onEditUser={canWrite ? handleEditUser : undefined}
          className="organisation-listing-card"
          hasAnyUsers={hasAnyUsers}
        />

        <RightSideModal
          isOpen={isAddModalOpen}
          onClose={handleModalClose}
          title={selectedUser ? "Edit User" : "Add User"}
          buttonlabel={"Save"}
          formId={formId}
          maxWidth={550}
          isDirty={isDirty}
          isSubmitDisabled={methods.formState.isSubmitting || !isDirty || !isValid}
          isLoading={methods.formState.isSubmitting}
        >
          <Card className="no-bg">
            <FormProvider {...methods}>
              <form id={formId} onSubmit={handleSubmit(onSubmit, (error) => {
                console.log("Form validation errors:", error);
              })}>
                <div className="row">
                  <div className="col-12">
                    <SelectField
                      placeholderText="Select User Role"
                      name="organizationUserTypeId"
                      label="User Role"
                      required
                      requiredMessage="User role is required"
                      options={userRoles}
                      labelAddon={
                        <button
                          type="button"
                          className="role-info-trigger"
                          aria-label="What can each role access?"
                          onClick={() => setIsRoleInfoOpen(true)}
                        >
                          <img src={GetImages.infoBlackIcon} alt="" width={16} height={16} />
                        </button>
                      }
                    />
                  </div>
                  <div className="col-12">
                    <InputField
                      name="title"
                      label="Title"
                      type="text"
                      placeholder="Enter Title"
                      required
                      requiredMessage="Title is required"
                      onlyAlphabets
                      allowSpecialChars={false}
                    />
                  </div>
                  <div className="col-md-6">
                    <InputField
                      name="firstName"
                      label="First Name"
                      type="text"
                      required
                      requiredMessage="First Name is required"
                      placeholder="Enter First Name"
                      onlyAlphabets
                      allowSpecialChars={false}
                      validation={{
                        minLength: {
                          value: 2,
                          message: "First name must be at least 2 characters long"
                        },
                        maxLength: {
                          value: 20,
                          message: "First name cannot exceed 20 characters"
                        }
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <InputField
                      name="lastName"
                      label="Last Name"
                      type="text"
                      required
                      requiredMessage="Last Name is required"
                      placeholder="Enter Last Name"
                      onlyAlphabets
                      allowSpecialChars={false}
                      validation={{
                        minLength: {
                          value: 2,
                          message: "Last name must be at least 2 characters long"
                        },
                        maxLength: {
                          value: 20,
                          message: "Last name cannot exceed 20 characters"
                        }
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <EmailInput
                      name="email"
                      label="Email Address"
                      required
                      requiredMessage="Email Address is required"
                      placeholder="Enter Email Address"
                    />
                  </div>
                  <div className="col-md-6">
                    <PhoneInput
                      key={whatsAppEnabled ? "phone-wa-all" : "phone-us-only"}
                      name="phoneNumber"
                      label="Contact No"
                      required={true}
                      requiredMessage="Contact No is required"
                      defaultCountry="us"
                      placeholder="+1 (555) 000-0000"
                      onlyCountries={whatsAppEnabled ? [] : ["us"]}
                    />
                    {whatsAppEnabled && (
                      <p className="sub_text grey_subtext1 mb-0 mt-1">
                        Used as the WhatsApp Business sender for Messaging.
                      </p>
                    )}
                  </div>
                </div>
              </form>
            </FormProvider>
          </Card>
        </RightSideModal>

        <Commonpopup
          isOpen={isRoleInfoOpen}
          onClose={() => setIsRoleInfoOpen(false)}
          component={<RoleAccessMatrix />}
          wide
        />
      </div>
    </PermissionGuard>
  );
};

export default ManageUsersPage;